import { supabase } from '@/lib/supabase';

/**
 * Thin wrapper over `supabase.rpc` for the `work_*` functions. They aren't in
 * the generated types, hence the casts.
 */
export async function workRpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.rpc(fn as never, args as never);
  if (error) throw new WorkError(error.message, error.code);
  return data as T;
}

export class WorkError extends Error {
  code: string | undefined;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'WorkError';
    this.code = code;
  }
}

/**
 * The RPCs raise / return short machine codes (`work_pin_invalid`, …). Map
 * them to something a worker can read on a tablet from arm's length.
 */
const MESSAGES: Record<string, string> = {
  work_forbidden: 'You are not allowed to do that.',
  work_auth_required: 'Enter your PIN or scan your badge to start.',
  work_user_required: 'Pick who you are first.',
  work_user_inactive: 'That account is not active. Ask a manager.',
  work_user_not_found: 'That person is not part of this organisation.',
  work_pin_not_set: 'No PIN yet. Ask a manager to set one under HR → Employees, or set it in Settings.',
  work_pin_invalid: 'That PIN is not right.',
  work_pin_locked: 'Too many tries. This PIN is locked for 15 minutes — ask a manager to unlock it.',
  work_pin_format: 'PINs are 4 to 8 digits.',
  work_badge_unknown: 'That badge is not assigned to anyone here.',
  work_badge_in_use: 'That badge already belongs to someone else.',
  work_badge_format: 'That does not look like a badge number.',
  work_station_not_found: 'This station no longer exists. Pick another one.',
  work_station_inactive: 'This station is switched off. Ask a manager.',
  work_session_not_found: 'This shift was closed. Sign in again.',
  work_session_ended: 'This shift has already ended. Sign in again.',
  work_session_forbidden: 'This shift belongs to a different tablet.',
  work_unit_too_fast: 'That was quick — give it a few seconds before logging a unit.',
  work_on_break: 'You are on a break. Resume first.',
  work_undo_unavailable: 'Nothing recent to undo.',
  work_description_required: 'Tell us what went wrong — a few words is enough.',
  work_scrap_tag_required: 'Tag the type of scrap — pick at least one.',
  work_body_required: 'Write a few words first.',
  work_mood_range: 'Pick how you feel today.',
  work_item_not_found: 'That item is not in the catalog.',
  work_photo_invalid: 'That photo could not be used. Upload it again.',
  work_item_required: 'Pick what you are building before logging a serialized unit.',
  work_serial_required: 'Scan or type the serial number first.',
  work_serial_duplicate: 'That serial number has already been logged. Check the label.',
  work_serial_photos_required: 'Take at least two photos of the finished unit.',
  work_qc_result_invalid: 'Mark the unit as passing or failing QC.',
  work_undo_serial_locked: 'That unit has already been inspected or shipped, so it cannot be undone from the bench.',
  serial_unit_not_found: 'That unit is not in the log.',
  serial_status_invalid: 'That is not a valid status for a unit.',
  serial_not_shipped: 'Only a shipped unit can be marked as returned.',
  serial_built_at_future: 'The build date cannot be in the future.',
  serial_sales_order_not_found: 'That sales order no longer exists.',
  serial_shipment_not_found: 'That shipment no longer exists.',
  serial_customer_not_found: 'That customer no longer exists.',
  serial_destination_required: 'Pick a sales order or customer, or type where the units went.',
  scrap_item_required: 'Pick the part that was scrapped.',
  scrap_time_invalid: 'Time already spent has to be a whole number of minutes, up to a week.',
  scrap_progress_invalid: 'How far along has to be a percent from 0 to 100.',
  scrap_photo_invalid: 'One of the photos could not be attached. Try again.',
  scrap_severity_invalid: 'Pick how bad the scrap is.',
  work_signoff_date_future: 'The sign-off date cannot be in the future.',
  work_signoff_not_found: 'That sign-off no longer exists.',
  work_signoff_revoked: 'That sign-off was already revoked.',
  uq_workstation_signoffs_active: 'Someone just signed this person off here. Refresh and try again.',
  ticket_title_required: 'Say what you need — a few words is enough.',
  ticket_priority_invalid: 'Pick how urgent this is.',
  ticket_not_found: 'That request is gone. Close this and try again.',
  ticket_forbidden: 'Only the person who asked can change this request.',
  ticket_forbidden_field: 'Only the person handling this request can change that.',
  ticket_requester_status: 'The person handling your request moves it along — you can reply here.',
};

export function describeWorkError(err: unknown): string {
  const raw =
    typeof err === 'string'
      ? err
      : err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : '';
  const code = raw.trim();
  if (MESSAGES[code]) return MESSAGES[code];
  // PostgREST wraps RAISE messages; find any known code inside.
  for (const key of Object.keys(MESSAGES)) {
    if (code.includes(key)) return MESSAGES[key];
  }
  return code || 'Something went wrong. Try again.';
}

/** mm:ss, or h:mm:ss once it passes an hour. */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

/** "4m 12s", "1h 05m", "38s" — for summaries, not the live timer. */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return '—';
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return sec ? `${m}m ${String(sec).padStart(2, '0')}s` : `${m}m`;
}

/**
 * Parse what a manager types into a target-time field: `12:30`, `12.5`,
 * `12 min`, `45s`. Empty = no target; anything else = 'invalid'.
 */
export function parseDurationInput(text: string): number | null | 'invalid' {
  const t = text.trim();
  if (!t) return null;
  const mmss = /^(\d+):([0-5]?\d)$/.exec(t);
  if (mmss) return Number(mmss[1]) * 60 + Number(mmss[2]);
  const mins = /^(\d+(?:\.\d+)?)\s*(m|min|mins)?$/.exec(t);
  if (mins) return Math.round(Number(mins[1]) * 60);
  const secs = /^(\d+)\s*s$/.exec(t);
  if (secs) return Number(secs[1]);
  return 'invalid';
}

/** The inverse of `parseDurationInput` for prefilling a field: `mm:ss`. */
export function durationInputText(seconds: number | null | undefined): string {
  if (!seconds) return '';
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function secondsSince(iso: string, now: number = Date.now()): number {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, (now - t) / 1000);
}
